import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createRoot } from 'react-dom/client';
import 'antd/dist/reset.css';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  ConfigProvider,
  Descriptions,
  Empty,
  Flex,
  Form,
  Input,
  List,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import {
  ArrowLeftOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { zhibiTheme } from '../shared/theme';
import './styles.css';

const { Title, Paragraph, Text, Link } = Typography;

type Platform = 'all' | 'twitter' | 'facebook' | 'telegram' | 'whatsapp';
type SourceType = 'all' | 'profile' | 'channel' | 'content';
type SortMode = 'relevance' | 'newest';
type DataScope = 'all' | 'foreground' | 'background';

type SearchSource = {
  alias: string;
  collection: string;
  mongo_id: string;
  detail_enabled: boolean;
  screen_name?: string;
  username?: string;
  channel_id?: string;
  user_id?: string;
};

type SearchResult = {
  platform: string;
  source_type: string;
  title: string;
  subtitle: string;
  snippet: string;
  contacts: string[];
  metadata: Record<string, string>;
  source: SearchSource;
  profile_source?: SearchSource | null;
  score?: number | null;
  access_state?: 'allowed' | 'locked';
  permission_message?: string;
  permission_action?: 'login' | 'request_permission';
  permission_url?: string;
};

type SearchResponse = {
  query: string;
  platform: Platform;
  source_type: SourceType;
  page: number;
  page_size: number;
  sort: SortMode;
  data_scope: DataScope;
  allowed_platforms: string[];
  total: number;
  total_relation: 'eq' | 'gte';
  facets: {
    platforms: Record<string, number>;
    source_types: Record<string, number>;
    data_scopes: Record<string, number>;
  };
  results: SearchResult[];
  has_more: boolean;
  backend: 'elasticsearch' | 'mongodb';
  diagnostics: string[];
  locked_count?: number;
  locked_platforms?: LockedResultGroup[];
  allowed_data_sources?: string[];
};

type DetailField = {
  label: string;
  value: string;
  source?: SearchSource | null;
  access_state?: 'allowed' | 'locked';
  permission_message?: string;
};

type RelatedAccount = {
  summary: SearchResult;
  matched_emails: string[];
  matched_phones: string[];
};

type RecentContent = {
  tweet_id: string;
  text: string;
  created_at: string;
  favorite_count: string;
  retweet_count: string;
  reply_count: string;
  lang: string;
};

type TweetContentItem = RecentContent & {
  mongo_id: string;
  platform?: string;
  content_id?: string;
  msg_id?: string;
  channel_id?: string;
  channel_title?: string;
  channel_username?: string;
  channel_source?: SearchSource | null;
  user_id?: string;
  author_screen_name: string;
  author_id: string;
  author_username?: string;
  author_display_name?: string;
  user_source?: SearchSource | null;
  quote_count: string;
  bookmark_count: string;
  view_count: string;
  conversation_id: string;
  in_reply_to_screen_name: string;
  source: string;
  database_updated_at: string;
  url: string;
};

type TweetContentResponse = {
  platform: string;
  source_type?: string;
  scope_type?: string;
  scope_id?: string;
  scope_label?: string;
  scope_source?: SearchSource | null;
  profile_source?: SearchSource | null;
  screen_name?: string;
  user_id?: string;
  display_name?: string;
  page: number;
  page_size: number;
  total: number;
  total_relation: 'eq' | 'gte';
  has_more: boolean;
  items: TweetContentItem[];
  ok?: boolean;
  message?: string;
  account_status?: string;
  account_status_reason?: string;
  permission_action?: 'login' | 'request_permission';
  permission_url?: string;
};

type ContentScopeSummary = {
  handle: string;
  authorId: string;
  displayName: string;
  channelId: string;
  userId: string;
  channelLabel: string;
  userLabel: string;
  telegramUsername: string;
};

type ContentMetaRow = [string, React.ReactNode];

type TweetDetailResponse = {
  platform: 'twitter';
  source_type: 'content';
  item: TweetContentItem;
  profile_source?: SearchSource | null;
  ok?: boolean;
  message?: string;
};

type PlatformDetail = {
  summary: SearchResult;
  identifiers: {
    emails: string[];
    phones: string[];
    usernames?: string[];
    restricted_emails_count?: number;
    restricted_phones_count?: number;
  };
  fields: DetailField[];
  related_accounts?: RelatedAccount[];
  twitter_profile?: Record<string, string> | null;
  recent_content: RecentContent[];
  access_state?: 'allowed' | 'locked';
  permission_message?: string;
  permission_action?: 'login' | 'request_permission';
  permission_url?: string;
  can_view_contacts?: boolean;
  matched_emails?: string[];
  matched_phones?: string[];
  restricted_matched_emails_count?: number;
  restricted_matched_phones_count?: number;
};

type EntityDetail = PlatformDetail & {
  related_accounts: RelatedAccount[];
  platform_details?: PlatformDetail[];
  twitter_profile: Record<string, string> | null;
  contact_info?: {
    emails: string[];
    phones: string[];
    restricted_emails_count?: number;
    restricted_phones_count?: number;
    can_view?: boolean;
    permission_action?: 'login' | 'request_permission';
    permission_url?: string;
  };
};

type SearchState = {
  query: string;
  platform: Platform;
  sourceType: SourceType;
  page: number;
  pageSize: number;
  sort: SortMode;
  dataScope: DataScope;
};

type CapabilitiesResponse = {
  allowed_platforms: string[];
  allowed_data_sources?: string[];
  public_data_sources?: string[];
};

type CachedSearchResponse = {
  cachedAt: number;
  response: SearchResponse;
};

type LockedResultGroup = {
  platform: string;
  count: number;
  permission_action?: 'login' | 'request_permission';
  permission_url?: string;
  url?: string;
};

const platformOptions: Array<[Platform, string]> = [
  ['all', '全部平台'],
  ['twitter', 'Twitter'],
  ['facebook', 'Facebook'],
  ['telegram', 'Telegram'],
  ['whatsapp', 'WhatsApp'],
];

const sourceTypeOptions: Array<[SourceType, string]> = [
  ['all', '全部类型'],
  ['profile', '账号'],
  ['channel', '频道'],
  ['content', '具体内容'],
];

const pageSizeOptions = [10, 20, 50];
const sortOptions: Array<[SortMode, string]> = [
  ['relevance', '相关度优先'],
  ['newest', '最新优先'],
];
const dataScopeOptions: Array<[DataScope, string]> = [
  ['all', '全部'],
  ['foreground', '前台数据'],
  ['background', '后台数据'],
];
const searchCachePrefix = 'zhibi:data-center:search:v11:';
const searchCacheTtlMs = 5 * 60 * 1000;
const internalMetadataLabels = new Set(['数据集合', '数据命名空间', 'namespace', 'collection']);
const defaultResponse: SearchResponse = {
  query: '',
  platform: 'all',
  source_type: 'all',
  page: 1,
  page_size: 20,
  sort: 'relevance',
  data_scope: 'all',
  allowed_platforms: ['twitter', 'facebook', 'telegram', 'whatsapp'],
  total: 0,
  total_relation: 'eq',
  facets: { platforms: {}, source_types: {}, data_scopes: {} },
  results: [],
  has_more: false,
  backend: 'elasticsearch',
  diagnostics: [],
};

function readInitialState(): SearchState {
  const params = new URLSearchParams(window.location.search);
  return {
    query: params.get('q') ?? '',
    platform: normalizePlatform(params.get('platform')),
    sourceType: normalizeSourceType(params.get('source_type')),
    page: normalizePositiveInt(params.get('page'), 1),
    pageSize: normalizePageSize(params.get('page_size')),
    sort: normalizeSort(params.get('sort')),
    dataScope: normalizeDataScope(params.get('data_scope')),
  };
}

function normalizePlatform(value: string | null): Platform {
  return ['twitter', 'facebook', 'telegram', 'whatsapp'].includes(value ?? '') ? (value as Platform) : 'all';
}

function normalizeSourceType(value: string | null): SourceType {
  return ['profile', 'channel', 'content'].includes(value ?? '') ? (value as SourceType) : 'all';
}

function normalizePositiveInt(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizePageSize(value: string | null): number {
  const parsed = normalizePositiveInt(value, 20);
  return pageSizeOptions.includes(parsed) ? parsed : 20;
}

function normalizeSort(value: string | null): SortMode {
  return value === 'newest' ? 'newest' : 'relevance';
}

function normalizeDataScope(value: string | null): DataScope {
  return ['foreground', 'background'].includes(value ?? '') ? (value as DataScope) : 'all';
}

function formatCount(value: number, relation: 'eq' | 'gte' = 'eq'): string {
  const formatted = new Intl.NumberFormat('zh-CN').format(value);
  return relation === 'gte' ? `${formatted}+` : formatted;
}

function facetCountLabel(
  counts: Record<string, number>,
  value: string,
  activeValue: string,
  showWhenAll = true,
): string {
  if (activeValue === 'all' && !showWhenAll) return '';
  if (activeValue !== 'all' && activeValue !== value) return '';
  const count = counts[value];
  return typeof count === 'number' ? formatCount(count) : '';
}

function detailUrl(source: SearchSource, searchParams = ''): string {
  const query = searchParams.replace(/^\?/, '');
  const baseUrl = `/data/detail/${encodeURIComponent(source.alias)}/${encodeURIComponent(source.collection)}/${encodeURIComponent(source.mongo_id)}/`;
  return query ? `${baseUrl}?${query}` : baseUrl;
}

function searchEngineUrl(searchParams = ''): string {
  const query = searchParams.replace(/^\?/, '');
  return query ? `/data/search-engine/?${query}` : '/data/search-engine/';
}

function currentSearchReturnUrl(searchParams = ''): string {
  const query = searchParams.replace(/^\?/, '');
  return query ? `/data/search-engine/?${query}` : '/data/search-engine/';
}

function tweetDetailUrl(source: SearchSource, searchParams = ''): string {
  if (source.alias !== 'twitter' || source.collection !== 'tweets' || !source.mongo_id) return '';
  const params = new URLSearchParams({
    return_to: currentSearchReturnUrl(searchParams),
  });
  return `/data/content/twitter/tweet/${encodeURIComponent(source.mongo_id)}/?${params}`;
}

function platformContentUrl(detail: PlatformDetail): string {
  const source = detail.summary.source;
  const username = source.screen_name || source.username || detail.identifiers.usernames?.[0] || '';
  const params = new URLSearchParams({
    return_to: `${window.location.pathname}${window.location.search}`,
  });
  if (source.user_id) params.set('user_id', source.user_id);
  if (detail.summary.platform === 'twitter' && username) {
    return `/data/content/twitter/${encodeURIComponent(username)}/?${params}`;
  }
  if (
    detail.summary.platform === 'telegram'
    && ['channel', 'profile'].includes(detail.summary.source_type)
    && source.alias
    && source.collection
    && source.mongo_id
  ) {
    return `/data/content/telegram/${encodeURIComponent(source.alias)}/${encodeURIComponent(source.collection)}/${encodeURIComponent(source.mongo_id)}/?${params}`;
  }
  return '';
}

function searchCacheKey(params: URLSearchParams, accessScope: string): string {
  return `${searchCachePrefix}${accessScope}:${params.toString()}`;
}

function readCachedSearch(params: URLSearchParams, accessScope: string): SearchResponse | null {
  try {
    const cacheKey = searchCacheKey(params, accessScope);
    const rawValue = window.sessionStorage.getItem(cacheKey);
    if (!rawValue) return null;
    const cached = JSON.parse(rawValue) as CachedSearchResponse;
    if (!cached.cachedAt || Date.now() - cached.cachedAt > searchCacheTtlMs) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }
    return cached.response;
  } catch {
    return null;
  }
}

function writeCachedSearch(params: URLSearchParams, accessScope: string, response: SearchResponse) {
  try {
    window.sessionStorage.setItem(searchCacheKey(params, accessScope), JSON.stringify({ cachedAt: Date.now(), response }));
  } catch {
    // sessionStorage can be unavailable or full; search still works without cache.
  }
}

function optionList<T extends string>(items: Array<[T, string]>) {
  return items.map(([value, label]) => ({ value, label }));
}

function platformLabel(value: string) {
  return platformOptions.find(([key]) => key === value)?.[1] ?? value;
}

function sourceTypeLabel(value: string) {
  return sourceTypeOptions.find(([key]) => key === value)?.[1] ?? value;
}

function detailPageLabel(value: string) {
  if (value === 'channel') return '频道详情';
  if (value === 'content') return '内容详情';
  return '用户详情';
}

function contentParentDetailLabel(platform: string, scopeType?: string, collection?: string | null) {
  if (platform === 'telegram' && (scopeType === 'channel' || collection === 'channel_info')) {
    return '频道详情';
  }
  return '用户详情';
}

function dataSourceLabel(source: Partial<SearchSource>, platform = '') {
  if (source.alias === 'twitter') return `${platformLabel(platform || 'twitter')} 原始数据`;
  if (source.alias === 'information' && platform === 'telegram') return 'Telegram 原始数据';
  if (source.alias === 'information') return `${platformLabel(platform || '')} 后台数据`;
  return platformLabel(platform || source.alias || '数据源');
}

function visibleMetadataEntries(metadata: Record<string, string>) {
  return Object.entries(metadata).filter(([key, value]) => value && !internalMetadataLabels.has(key));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightTerms(query: string): string[] {
  const normalized = query.trim();
  if (!normalized) return [];
  const parts = normalized.split(/\s+/).filter((item) => item.length >= 2);
  return Array.from(new Set([normalized, ...parts])).sort((a, b) => b.length - a.length);
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const terms = highlightTerms(query);
  if (!text || terms.length === 0) return <>{text}</>;
  const matcher = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  return (
    <>
      {text.split(matcher).map((part, index) => {
        const isMatch = terms.some((term) => part.toLowerCase() === term.toLowerCase());
        return isMatch ? <mark className="zhibi-highlight" key={`${part}-${index}`}>{part}</mark> : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
      })}
    </>
  );
}

function groupLockedResults(results: SearchResult[]): LockedResultGroup[] {
  const groups = new Map<string, LockedResultGroup>();
  results.filter((item) => item.access_state === 'locked').forEach((item) => {
    const platform = item.platform || 'unknown';
    const current = groups.get(platform);
    if (current) {
      current.count += 1;
      return;
    }
    groups.set(platform, {
      platform,
      count: 1,
      permission_action: item.permission_action,
      permission_url: item.permission_url,
    });
  });
  return Array.from(groups.values()).sort((a, b) => b.count - a.count);
}

function RestrictedSummary({ groups }: { groups: LockedResultGroup[] }) {
  if (groups.length === 0) return null;
  const total = groups.reduce((sum, group) => sum + group.count, 0);
  const loginGroup = groups.find((group) => group.permission_action === 'login' && (group.permission_url || group.url));
  const platformText = groups.map((group) => `${platformLabel(group.platform)} ${formatCount(group.count)} 条`).join('，');
  return (
    <Alert
      className="zhibi-alert zhibi-restricted-alert"
      type="warning"
      showIcon
      message={`受限结果：共 ${formatCount(total)} 条`}
      description={`本次检索命中 ${platformText} 受限结果。${loginGroup ? '登录后可按账号权限查看完整内容。' : '当前账号暂无权限查看，请联系管理员开通。'}`}
      action={loginGroup ? <Button size="small" href={loginGroup.permission_url || loginGroup.url}>登录后查看</Button> : undefined}
    />
  );
}

function SearchApp() {
  const [state, setState] = useState<SearchState>(() => readInitialState());
  const [draft, setDraft] = useState<SearchState>(() => readInitialState());
  const [response, setResponse] = useState<SearchResponse>(defaultResponse);
  const [allowedPlatforms, setAllowedPlatforms] = useState<Platform[]>(['twitter', 'facebook', 'telegram', 'whatsapp']);
  const [allowedDataSources, setAllowedDataSources] = useState<string[]>([]);
  const [capabilitiesReady, setCapabilitiesReady] = useState(false);
  const [error, setError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isPending, startTransition] = useTransition();
  const searchRequestId = useRef(0);

  const hasQuery = state.query.trim().length > 0;
  const isSearchLoading = hasQuery && (!capabilitiesReady || isSearching || isPending);
  const visiblePlatformOptions = useMemo(
    () => platformOptions.filter(([value]) => value === 'all' || allowedPlatforms.includes(value)),
    [allowedPlatforms],
  );

  useEffect(() => {
    fetch('/data/api/capabilities/')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<CapabilitiesResponse>;
      })
      .then((payload) => {
        const platforms = payload.allowed_platforms.filter((value): value is Platform => normalizePlatform(value) !== 'all');
        setAllowedPlatforms(platforms);
        setAllowedDataSources(payload.allowed_data_sources ?? []);
        if (state.platform !== 'all' && !platforms.includes(state.platform)) {
          setState((current) => ({ ...current, platform: 'all', page: 1 }));
          setDraft((current) => ({ ...current, platform: 'all', page: 1 }));
        }
      })
      .catch(() => setError('无法读取当前账号的数据权限，请刷新页面或联系管理员。'))
      .finally(() => setCapabilitiesReady(true));
  }, [state.platform]);

  const params = useMemo(() => {
    const next = new URLSearchParams();
    if (state.query.trim()) next.set('q', state.query.trim());
    if (state.platform !== 'all') next.set('platform', state.platform);
    if (state.sourceType !== 'all') next.set('source_type', state.sourceType);
    if (state.page > 1) next.set('page', String(state.page));
    if (state.pageSize !== 20) next.set('page_size', String(state.pageSize));
    if (state.sort !== 'relevance') next.set('sort', state.sort);
    if (state.dataScope !== 'all') next.set('data_scope', state.dataScope);
    return next;
  }, [state]);
  const accessScope = useMemo(() => allowedDataSources.join(','), [allowedDataSources]);

  useEffect(() => {
    const nextUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState(null, '', nextUrl);
  }, [params]);

  useEffect(() => {
    if (!hasQuery) {
      setResponse(defaultResponse);
      setError('');
      setIsSearching(false);
      return;
    }
    if (!capabilitiesReady) return;

    const controller = new AbortController();
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    const requestParams = new URLSearchParams({
      q: state.query.trim(),
      platform: state.platform,
      source_type: state.sourceType,
      page: String(state.page),
      page_size: String(state.pageSize),
      sort: state.sort,
      data_scope: state.dataScope,
    });
    const cachedResponse = readCachedSearch(requestParams, accessScope);
    if (cachedResponse) {
      startTransition(() => setResponse(cachedResponse));
      setError('');
      setIsSearching(false);
      return;
    }

    setError('');
    setIsSearching(true);
    fetch(`/data/api/search/?${requestParams}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then((payload: { message?: string }) => {
            throw new Error(payload.message || `HTTP ${res.status}`);
          });
        }
        return res.json() as Promise<SearchResponse>;
      })
      .then((payload) => {
        writeCachedSearch(requestParams, accessScope, payload);
        startTransition(() => setResponse(payload));
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message || '检索服务暂时不可用，请稍后重试。');
      })
      .finally(() => {
        if (searchRequestId.current === requestId) {
          setIsSearching(false);
        }
      });

    return () => controller.abort();
  }, [accessScope, capabilitiesReady, hasQuery, state]);

  function submitSearch() {
    setState({ ...draft, query: draft.query.trim(), page: 1 });
  }

  function resetSearch() {
    const next = { query: '', platform: 'all' as Platform, sourceType: 'all' as SourceType, page: 1, pageSize: 20, sort: 'relevance' as SortMode, dataScope: 'all' as DataScope };
    setState(next);
    setDraft(next);
  }

  function goToPage(page: number, pageSize = state.pageSize) {
    setState((current) => ({ ...current, page: Math.max(1, page), pageSize }));
    setDraft((current) => ({ ...current, page: Math.max(1, page), pageSize }));
  }

  function applyPlatform(platform: Platform) {
    setState((current) => ({ ...current, platform, page: 1 }));
    setDraft((current) => ({ ...current, platform, page: 1 }));
  }

  function applySourceType(sourceType: SourceType) {
    setState((current) => ({ ...current, sourceType, page: 1 }));
    setDraft((current) => ({ ...current, sourceType, page: 1 }));
  }

  function applyDataScope(dataScope: DataScope) {
    setState((current) => ({ ...current, dataScope, page: 1 }));
    setDraft((current) => ({ ...current, dataScope, page: 1 }));
  }

  const lockedGroups = response.locked_platforms?.length ? response.locked_platforms : groupLockedResults(response.results);

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Breadcrumb
            className="zhibi-page-nav"
            items={[
              { title: <a href="/">首页</a> },
              { title: '数据中心' },
              { title: '跨平台检索' },
            ]}
          />
          <Row gutter={[24, 16]} align="middle">
            <Col flex="auto">
              <Title level={2}>跨平台数据中心</Title>
              <Paragraph type="secondary">
                统一检索账号、频道和公开内容。部分结果会根据账号权限展示完整信息或访问提示。
              </Paragraph>
            </Col>
            <Col>
              <Statistic title="命中结果" value={hasQuery && !isSearchLoading ? formatCount(response.total, response.total_relation) : 0} prefix={<SearchOutlined />} />
              <Text type="secondary">{isSearchLoading ? '正在检索' : hasQuery ? '已完成检索' : '等待检索'}</Text>
            </Col>
          </Row>
        </Card>

        <Card className="zhibi-section-card">
          <Form layout="vertical" onFinish={submitSearch} className="zhibi-search-form">
            <Row gutter={[12, 8]} align="bottom">
              <Col xs={24} xl={18}>
                <Form.Item label="检索词">
                  <Input
                    allowClear
                    size="large"
                    prefix={<SearchOutlined />}
                    value={draft.query}
                    onChange={(event) => setDraft({ ...draft, query: event.target.value })}
                    placeholder="输入用户名、句柄、邮箱、手机号、频道名或 Twitter 关键词"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} xl={6}>
                <Form.Item label="操作">
                  <Space.Compact block>
                    <Button type="primary" htmlType="submit" block>搜索</Button>
                    <Button onClick={resetSearch}>重置</Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 8]}>
              <Col xs={12} md={6}>
                <Form.Item label="平台">
                  <Select value={draft.platform} onChange={(platform) => setDraft({ ...draft, platform })} options={optionList(visiblePlatformOptions)} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="类型">
                  <Select value={draft.sourceType} onChange={(sourceType) => setDraft({ ...draft, sourceType })} options={optionList(sourceTypeOptions)} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="每页">
                  <Select value={draft.pageSize} onChange={(pageSize) => setDraft({ ...draft, pageSize })} options={pageSizeOptions.map((value) => ({ value, label: String(value) }))} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="排序">
                  <Select value={draft.sort} onChange={(sort) => setDraft({ ...draft, sort })} options={optionList(sortOptions)} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}

        <Card className="zhibi-section-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Text type="secondary">检索结果</Text>
              <Title level={3}>{hasQuery ? `“${state.query}” 的检索结果` : '开始一次统一检索'}</Title>
              {hasQuery && !isSearchLoading && <Text type="secondary">共 {formatCount(response.total, response.total_relation)} 条命中 · 当前第 {response.page} 页 · {sortOptions.find(([value]) => value === response.sort)?.[1] ?? '相关度优先'}</Text>}
              {isSearchLoading && <Text type="secondary">正在从数据索引中检索，请稍候。</Text>}
            </div>
            {isSearchLoading && <Spin />}
          </Flex>
          {hasQuery && !isSearchLoading && <RestrictedSummary groups={lockedGroups} />}

          {hasQuery && !isSearchLoading && (
            <FacetPanel
              facets={response.facets}
              activePlatform={state.platform}
              activeSourceType={state.sourceType}
              activeDataScope={state.dataScope}
              allowedPlatforms={allowedPlatforms}
              onPlatformChange={applyPlatform}
              onSourceTypeChange={applySourceType}
              onDataScopeChange={applyDataScope}
            />
          )}

          {!hasQuery && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="输入检索词后开始搜索" />}
          {isSearchLoading && (
            <div className="zhibi-search-loading">
              <Spin size="large" tip="正在检索数据，请稍候..." />
              <Text type="secondary">数据量较大时可能需要几秒钟。</Text>
            </div>
          )}
          {hasQuery && !isSearchLoading && !error && response.results.length === 0 && <Empty description="没有搜到结果，可以尝试缩短关键词或切换平台。" />}
          {!isSearchLoading && response.results.length > 0 && <ResultList results={response.results} searchParams={params.toString()} query={state.query} />}

          {hasQuery && !isSearchLoading && response.results.length > 0 && (
            <Pagination
              className="zhibi-pagination"
              current={response.page}
              pageSize={response.page_size}
              total={response.has_more ? response.page * response.page_size + 1 : response.total}
              showSizeChanger
              pageSizeOptions={pageSizeOptions}
              onChange={goToPage}
            />
          )}
        </Card>
      </div>
    </ConfigProvider>
  );
}

function FacetPanel({
  facets,
  activePlatform,
  activeSourceType,
  activeDataScope,
  allowedPlatforms,
  onPlatformChange,
  onSourceTypeChange,
  onDataScopeChange,
}: {
  facets: SearchResponse['facets'];
  activePlatform: Platform;
  activeSourceType: SourceType;
  activeDataScope: DataScope;
  allowedPlatforms: Platform[];
  onPlatformChange: (platform: Platform) => void;
  onSourceTypeChange: (sourceType: SourceType) => void;
  onDataScopeChange: (dataScope: DataScope) => void;
}) {
  const platformCounts = facets.platforms ?? {};
  const sourceTypeCounts = facets.source_types ?? {};
  const dataScopeCounts = facets.data_scopes ?? {};
  return (
    <Space direction="vertical" className="zhibi-facets" size={12}>
      <Space wrap>
        <Text type="secondary">平台聚合</Text>
        <Button size="small" type={activePlatform === 'all' ? 'primary' : 'default'} onClick={() => onPlatformChange('all')}>全部</Button>
        {platformOptions.filter(([value]) => value !== 'all' && allowedPlatforms.includes(value)).map(([value, label]) => (
          <Button size="small" type={activePlatform === value ? 'primary' : 'default'} key={value} onClick={() => onPlatformChange(value)}>
            {label} {facetCountLabel(platformCounts, value, activePlatform) && <Text type="secondary">{facetCountLabel(platformCounts, value, activePlatform)}</Text>}
          </Button>
        ))}
      </Space>
      <Space wrap>
        <Text type="secondary">类型聚合</Text>
        <Button size="small" type={activeSourceType === 'all' ? 'primary' : 'default'} onClick={() => onSourceTypeChange('all')}>全部</Button>
        {sourceTypeOptions.filter(([value]) => value !== 'all').map(([value, label]) => (
          <Button size="small" type={activeSourceType === value ? 'primary' : 'default'} key={value} onClick={() => onSourceTypeChange(value)}>
            {label} {facetCountLabel(sourceTypeCounts, value, activeSourceType, false) && <Text type="secondary">{facetCountLabel(sourceTypeCounts, value, activeSourceType, false)}</Text>}
          </Button>
        ))}
      </Space>
      <Space wrap>
        <Text type="secondary">数据源聚合</Text>
        {dataScopeOptions.map(([value, label]) => (
          <Button size="small" type={activeDataScope === value ? 'primary' : 'default'} key={value} onClick={() => onDataScopeChange(value)}>
            {label}
            {value !== 'all' && facetCountLabel(dataScopeCounts, value, activeDataScope) && <Text type="secondary"> {facetCountLabel(dataScopeCounts, value, activeDataScope)}</Text>}
          </Button>
        ))}
      </Space>
    </Space>
  );
}

function ResultList({ results, searchParams, query }: { results: SearchResult[]; searchParams: string; query: string }) {
  const visibleResults = results.filter((item) => item.access_state !== 'locked');
  return (
    <>
      {visibleResults.length > 0 && (
        <List
          className="zhibi-result-list"
          itemLayout="vertical"
          dataSource={visibleResults}
          renderItem={(item) => {
            const metadataEntries = visibleMetadataEntries(item.metadata).slice(0, 4);
            const accountDetailUrl = item.source.detail_enabled ? detailUrl(item.source, searchParams) : '';
            const profileUrl = item.profile_source?.detail_enabled ? detailUrl(item.profile_source, searchParams) : '';
            const itemDetailUrl = tweetDetailUrl(item.source, searchParams);
            return (
              <List.Item>
                <div className="zhibi-result-row">
                  <div className="zhibi-result-main">
                    <Space wrap size={[6, 6]}>
                      <Tag color="blue">{platformLabel(item.platform)}</Tag>
                      <Tag>{sourceTypeLabel(item.source_type)}</Tag>
                      <Tag color={item.source.alias === 'information' ? 'gold' : 'default'}>{dataSourceLabel(item.source, item.platform)}</Tag>
                    </Space>
                    <Title level={4} className="zhibi-result-title">
                      {item.source_type === 'content' && profileUrl ? (
                        <Link href={profileUrl}><HighlightedText text={item.title || '未命名账号'} query={query} /></Link>
                      ) : accountDetailUrl ? (
                        <Link href={accountDetailUrl}><HighlightedText text={item.title || '未命名账号'} query={query} /></Link>
                      ) : (
                        <Text><HighlightedText text={item.title || '未命名内容'} query={query} /></Text>
                      )}
                    </Title>
                    {item.subtitle && (
                      item.source_type === 'content' && profileUrl ? (
                        <Link href={profileUrl} className="zhibi-result-subtitle"><HighlightedText text={item.subtitle} query={query} /></Link>
                      ) : accountDetailUrl ? (
                        <Link href={accountDetailUrl} className="zhibi-result-subtitle"><HighlightedText text={item.subtitle} query={query} /></Link>
                      ) : (
                        <Text type="secondary" className="zhibi-result-subtitle"><HighlightedText text={item.subtitle} query={query} /></Text>
                      )
                    )}
                    <Paragraph className="zhibi-result-snippet">
                      {itemDetailUrl ? (
                        <Link href={itemDetailUrl}><HighlightedText text={item.snippet || '暂无摘要'} query={query} /></Link>
                      ) : accountDetailUrl ? (
                        <Link href={accountDetailUrl}><HighlightedText text={item.snippet || '暂无摘要'} query={query} /></Link>
                      ) : (
                        <HighlightedText text={item.snippet || '暂无摘要'} query={query} />
                      )}
                    </Paragraph>
                    {itemDetailUrl && <Link className="zhibi-result-detail-hint" href={itemDetailUrl}>查看详情</Link>}
                  </div>
                  <div className="zhibi-result-side">
                    <Text type="secondary">来源</Text>
                    <strong>{dataSourceLabel(item.source, item.platform)}</strong>
                    {metadataEntries.length > 0 && (
                      <Descriptions size="small" column={1} className="zhibi-result-meta">
                        {metadataEntries.map(([key, value]) => (
                          <Descriptions.Item label={key} key={key}><HighlightedText text={value} query={query} /></Descriptions.Item>
                        ))}
                      </Descriptions>
                    )}
                    {metadataEntries.length === 0 && <Text type="secondary">暂无更多公开摘要</Text>}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </>
  );
}

function EntityDetailApp({ alias, collection, mongoId }: { alias: string; collection: string; mongoId: string }) {
  const [detail, setDetail] = useState<EntityDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`/data/api/detail/${encodeURIComponent(alias)}/${encodeURIComponent(collection)}/${encodeURIComponent(mongoId)}/`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then((payload: { message?: string }) => {
            throw new Error(payload.message || `HTTP ${res.status}`);
          });
        }
        return res.json() as Promise<EntityDetail>;
      })
      .then((payload) => setDetail(payload))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message || '详情加载失败。');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [alias, collection, mongoId]);

  if (loading) {
    return <ConfigProvider theme={zhibiTheme} locale={zhCN}><div className="zhibi-react-page"><Spin tip="正在加载详情..." /></div></ConfigProvider>;
  }

  if (error || !detail) {
    const returnUrl = searchEngineUrl(window.location.search);
    return (
      <ConfigProvider theme={zhibiTheme} locale={zhCN}>
        <div className="zhibi-react-page">
          <Alert type="error" showIcon message={error || '未找到对应账号详情。'} />
          <Button href={returnUrl} icon={<ArrowLeftOutlined />} className="zhibi-top-space">返回跨平台检索</Button>
        </div>
      </ConfigProvider>
    );
  }

  const currentSearchParams = window.location.search;
  const returnUrl = searchEngineUrl(currentSearchParams);
  const platformDetails = detail.platform_details?.length ? detail.platform_details : [detail];
  const lockedPlatformCount = platformDetails.filter((item) => item.access_state === 'locked').length;
  const visiblePlatformCount = platformDetails.length - lockedPlatformCount;
  const restrictedContactCount = (detail.contact_info?.restricted_emails_count ?? 0) + (detail.contact_info?.restricted_phones_count ?? 0);
  const currentDetailLabel = detailPageLabel(detail.summary.source_type);

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Flex className="zhibi-page-nav" justify="space-between" align="center" gap={12} wrap="wrap">
            <Breadcrumb
              items={[
                { title: <a href="/">首页</a> },
                { title: '数据中心' },
                { title: <a href={returnUrl}>跨平台检索</a> },
                { title: currentDetailLabel },
              ]}
            />
            <Button href={returnUrl} icon={<ArrowLeftOutlined />}>返回跨平台检索</Button>
          </Flex>
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Title level={2}>{detail.summary.title || '未命名账号'}</Title>
              {detail.summary.subtitle && <Paragraph type="secondary">{detail.summary.subtitle}</Paragraph>}
              <Space wrap>
                {platformDetails.map((item) => <Tag color="blue" key={`${item.summary.source.alias}:${item.summary.source.collection}:${item.summary.source.mongo_id}`}>{platformLabel(item.summary.platform)}</Tag>)}
                <Tag>{sourceTypeLabel(detail.summary.source_type)}</Tag>
                {platformDetails.length > 1 && <Tag color="green">已聚合 {platformDetails.length} 条账号数据</Tag>}
                <Tag color="green">可查看 {visiblePlatformCount} 个</Tag>
                {lockedPlatformCount > 0 && <Tag color="warning">受限 {lockedPlatformCount} 个</Tag>}
                {restrictedContactCount > 0 && <Tag color="warning">联系方式需授权</Tag>}
              </Space>
            </div>
          </Flex>
        </Card>

        <Space direction="vertical" size={16} className="zhibi-full-width">
          <ContactInfoCard contactInfo={detail.contact_info} />
          <Card title="账号详细数据" className="zhibi-section-card">
            <Space direction="vertical" size={16} className="zhibi-full-width">
              {platformDetails.map((item) => (
                <PlatformDetailCard
                  detail={item}
                  key={`${item.summary.source.alias}:${item.summary.source.collection}:${item.summary.source.mongo_id}`}
                />
              ))}
            </Space>
          </Card>
        </Space>
      </div>
    </ConfigProvider>
  );
}

function ContactInfoCard({ contactInfo }: { contactInfo?: EntityDetail['contact_info'] }) {
  const emails = contactInfo?.emails ?? [];
  const phones = contactInfo?.phones ?? [];
  const restrictedEmails = contactInfo?.restricted_emails_count ?? 0;
  const restrictedPhones = contactInfo?.restricted_phones_count ?? 0;
  const total = emails.length + phones.length + restrictedEmails + restrictedPhones;
  if (total === 0) return null;
  const loginUrl = contactInfo?.permission_action === 'login' ? contactInfo.permission_url : undefined;
  return (
    <Card title="联系方式" className="zhibi-section-card zhibi-contact-card">
      <Space direction="vertical" size={10} className="zhibi-full-width">
        <Space wrap>
          {emails.map((email) => <Tag key={`email:${email}`}>邮箱：{email}</Tag>)}
          {phones.map((phone) => <Tag key={`phone:${phone}`}>手机号：{phone}</Tag>)}
          {restrictedEmails > 0 && <Tag color="warning"><span className="zhibi-blurred-text">邮箱</span> {restrictedEmails} 个需授权</Tag>}
          {restrictedPhones > 0 && <Tag color="warning"><span className="zhibi-blurred-text">手机号</span> {restrictedPhones} 个需授权</Tag>}
        </Space>
        {(restrictedEmails > 0 || restrictedPhones > 0) && (
          <Text type="secondary">
            邮箱和手机号需要单独授权后查看。{loginUrl && <Link href={loginUrl}>登录后查看</Link>}
          </Text>
        )}
      </Space>
    </Card>
  );
}

function platformSourceLabel(detail: PlatformDetail): string {
  const source = detail.summary.source;
  if (detail.summary.platform === 'twitter' && source.alias === 'information') {
    return 'Twitter 后台数据';
  }
  if (detail.summary.platform === 'twitter' && source.alias === 'twitter') {
    return 'Twitter 原始数据';
  }
  if (detail.summary.platform === 'telegram' && source.alias === 'information') {
    return 'Telegram 原始数据';
  }
  return platformLabel(detail.summary.platform);
}

function PlatformDetailCard({ detail }: { detail: PlatformDetail }) {
  const isLocked = detail.access_state === 'locked';
  const source = detail.summary.source;
  const contentUrl = !isLocked ? platformContentUrl(detail) : '';
  const restrictedRelationCount = (detail.restricted_matched_emails_count ?? 0) + (detail.restricted_matched_phones_count ?? 0);
  return (
    <Card
      className={`zhibi-platform-detail-card ${isLocked ? 'zhibi-platform-detail-locked' : ''}`}
      title={(
        <Space wrap>
          <Tag color="blue">{platformLabel(detail.summary.platform)}</Tag>
          <Tag color={source.alias === 'information' ? 'gold' : 'default'}>{platformSourceLabel(detail)}</Tag>
          <span>{detail.summary.title || '未命名账号'}</span>
        </Space>
      )}
      extra={contentUrl ? <Link href={contentUrl}>{detail.summary.source_type === 'channel' ? '查看频道内容' : '查看账号内容'}</Link> : null}
    >
      {isLocked ? (
        <LockedDetailPanel detail={detail} />
      ) : (
        <Space direction="vertical" size={14} className="zhibi-full-width">
          {detail.summary.subtitle && <Text type="secondary">{detail.summary.subtitle}</Text>}
          <Paragraph>{detail.summary.snippet || '暂无摘要'}</Paragraph>
          {restrictedRelationCount > 0 && (
            <Alert
              type="info"
              showIcon
              message={`该账号通过 ${restrictedRelationCount} 条联系方式线索与其他平台关联，联系方式需单独授权后查看。`}
            />
          )}
          <Descriptions bordered column={{ xs: 1, md: 2 }} size="small">
            <Descriptions.Item label="数据来源">{platformSourceLabel(detail)}</Descriptions.Item>
            {detail.fields.map((field) => (
              <Descriptions.Item label={field.label} key={`${detail.summary.platform}:${field.label}:${field.value}`}>
                <ProtectedFieldValue field={field} />
              </Descriptions.Item>
            ))}
          </Descriptions>
          {detail.recent_content.length > 0 && <RecentContentList items={detail.recent_content} />}
        </Space>
      )}
    </Card>
  );
}

function ProtectedFieldValue({ field }: { field: DetailField }) {
  if (field.access_state === 'locked') {
    return (
      <Space size={8} wrap>
        <span className="zhibi-blurred-text">{field.value}</span>
        <Text type="secondary">{field.permission_message || '需授权查看'}</Text>
      </Space>
    );
  }
  if (field.source?.detail_enabled) {
    return <Link href={detailUrl(field.source)}>{field.value}</Link>;
  }
  return <>{field.value}</>;
}

function LockedDetailPanel({ detail }: { detail: PlatformDetail }) {
  const loginUrl = detail.permission_action === 'login' ? detail.permission_url : undefined;
  return (
    <div className="zhibi-detail-locked-panel">
      <Alert
        type="warning"
        showIcon
        message={detail.permission_message || '当前账号暂无权限查看该平台数据。'}
        action={loginUrl ? <Button size="small" href={loginUrl}>登录后查看</Button> : undefined}
      />
      <div className="zhibi-detail-blur-box">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function RecentContentList({ items }: { items: RecentContent[] }) {
  return (
    <Card type="inner" title="近期 Twitter 内容">
      <List
        dataSource={items}
        renderItem={(content) => (
          <List.Item>
            <List.Item.Meta title={`${content.created_at} · ${content.lang}`} description={content.text} />
            <Space wrap>
              <Tag>点赞 {content.favorite_count}</Tag>
              <Tag>转发 {content.retweet_count}</Tag>
              <Tag>回复 {content.reply_count}</Tag>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
}

function readReturnUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('return_to');
  if (returnTo?.startsWith('/')) return returnTo;
  return '/data/search-engine/';
}

function twitterProfileUrl(screenName: string): string {
  return `https://x.com/${encodeURIComponent(screenName)}`;
}

function twitterSearchUrl(screenName: string): string {
  const params = new URLSearchParams({
    q: `@${screenName}`,
    src: 'typed_query',
  });
  return `https://x.com/search?${params.toString()}`;
}

function EntityContentApp({
  platform,
  alias,
  collection,
  mongoId,
  screenName,
}: {
  platform: string;
  alias?: string;
  collection?: string;
  mongoId?: string;
  screenName?: string;
}) {
  const [response, setResponse] = useState<TweetContentResponse | null>(null);
  const [page, setPage] = useState(() => normalizePositiveInt(new URLSearchParams(window.location.search).get('page'), 1));
  const [pageSize, setPageSize] = useState(() => normalizePageSize(new URLSearchParams(window.location.search).get('page_size')));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const returnUrl = readReturnUrl();

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    const currentParams = new URLSearchParams(window.location.search);
    const userId = currentParams.get('user_id');
    if (userId) params.set('user_id', userId);
    const apiUrl = platform === 'twitter' && screenName
      ? `/data/api/content/twitter/${encodeURIComponent(screenName)}/?${params}`
      : `/data/api/content/${encodeURIComponent(platform)}/${encodeURIComponent(alias ?? '')}/${encodeURIComponent(collection ?? '')}/${encodeURIComponent(mongoId ?? '')}/?${params}`;
    setLoading(true);
    setError('');
    fetch(apiUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then((payload: TweetContentResponse) => {
            throw new Error(payload.message || `HTTP ${res.status}`);
          });
        }
        return res.json() as Promise<TweetContentResponse>;
      })
      .then((payload) => setResponse(payload))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message || '发帖内容加载失败。');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [alias, collection, mongoId, page, pageSize, platform, screenName]);

  function changePage(nextPage: number, nextPageSize = pageSize) {
    setPage(Math.max(1, nextPage));
    setPageSize(nextPageSize);
    const params = new URLSearchParams(window.location.search);
    if (nextPage > 1) params.set('page', String(nextPage));
    else params.delete('page');
    if (nextPageSize !== 20) params.set('page_size', String(nextPageSize));
    else params.delete('page_size');
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  }

  const total = response?.total ?? 0;
  const items = response?.items ?? [];
  const currentPlatform = response?.platform || platform;
  const currentScreenName = response?.screen_name || screenName || '';
  const twitterAccountUnavailable = response?.account_status === 'unavailable';
  const twitterExternalUrl = twitterAccountUnavailable
    ? twitterSearchUrl(currentScreenName)
    : twitterProfileUrl(currentScreenName);
  const twitterExternalLabel = twitterAccountUnavailable
    ? `在 Twitter/X 搜索 @${currentScreenName}`
    : `去 Twitter/X 查看 @${currentScreenName}`;
  const title = response?.scope_label
    ? `${response.scope_label} 的内容`
    : currentScreenName
      ? `@${currentScreenName} 的发帖内容`
      : '内容列表';
  const description = currentPlatform === 'telegram'
    ? (response?.scope_type === 'channel' ? '按频道读取 Telegram 消息记录，展示正文、时间和来源信息。' : '按账号读取 Telegram 消息记录，展示正文、时间和来源信息。')
    : '按账号读取 Twitter 推文记录，展示每条推文的正文、时间和互动数据。';
  const listTitle = currentPlatform === 'telegram' ? '内容列表' : '推文列表';
  const scopeSummary = buildContentScopeSummary(response, items, currentPlatform, screenName);
  const profileSource = response?.profile_source ?? null;
  const scopeSource = response?.scope_source ?? null;
  const parentDetailLabel = contentParentDetailLabel(currentPlatform, response?.scope_type, collection);

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Flex className="zhibi-page-nav" justify="space-between" align="center" gap={12} wrap="wrap">
            <Breadcrumb
              items={[
                { title: <a href="/">首页</a> },
                { title: '数据中心' },
                { title: <a href={returnUrl}>{parentDetailLabel}</a> },
                { title: '发帖内容' },
              ]}
            />
            <Button href={returnUrl} icon={<ArrowLeftOutlined />}>返回{parentDetailLabel}</Button>
          </Flex>
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Space wrap>
                <Tag color="blue">{platformLabel(currentPlatform)}</Tag>
                <Tag>具体内容</Tag>
              </Space>
              <Title level={2}>{title}</Title>
              <Paragraph type="secondary">{description}</Paragraph>
            </div>
            <Statistic title="内容记录" value={formatCount(total, response?.total_relation ?? 'eq')} />
          </Flex>
        </Card>

        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}

        <Card className="zhibi-section-card" title={listTitle}>
          {loading && <Spin tip="正在加载内容..." />}
          {!loading && !error && items.length === 0 && (
            <Empty
              description={
                currentPlatform === 'twitter' && currentScreenName
                  ? (
                    <Space direction="vertical" size={8}>
                      <Text>{response?.message || '本地推文库暂无该账号的发文记录。'}</Text>
                      <Link href={twitterExternalUrl} target="_blank" rel="noreferrer">
                        {twitterExternalLabel}
                      </Link>
                    </Space>
                  )
                  : (response?.message || '当前对象没有可展示的内容记录。')
              }
            />
          )}
          {!loading && items.length > 0 && (
            <>
              <ContentScopeSummaryPanel
                summary={scopeSummary}
                platform={currentPlatform}
                profileSource={profileSource}
                scopeSource={scopeSource}
              />
              <List
                className="zhibi-tweet-list"
                itemLayout="vertical"
                dataSource={items}
                renderItem={(item) => (
                  <TweetContentListItem
                    item={item}
                    scopeSummary={scopeSummary}
                    authorProfileSource={profileSource}
                  />
                )}
              />
              <Pagination
                className="zhibi-pagination"
                current={response?.page ?? page}
                pageSize={response?.page_size ?? pageSize}
                total={response?.has_more ? page * pageSize + 1 : total}
                showSizeChanger
                pageSizeOptions={pageSizeOptions}
                locale={{ items_per_page: '条/页' }}
                onChange={changePage}
              />
            </>
          )}
        </Card>
      </div>
    </ConfigProvider>
  );
}

function commonItemValue(items: TweetContentItem[], key: keyof TweetContentItem): string {
  const values = items
    .map((item) => item[key])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  if (values.length === 0) return '';
  return values.every((value) => value === values[0]) ? values[0] : '';
}

function buildContentScopeSummary(
  response: TweetContentResponse | null,
  items: TweetContentItem[],
  platform: string,
  screenName?: string,
): ContentScopeSummary {
  const authorScreenName = response?.screen_name || screenName || commonItemValue(items, 'author_screen_name');
  if (platform === 'telegram') {
    const scopeLabel = response?.scope_label || '';
    return {
      handle: '',
      authorId: '',
      displayName: '',
      channelId: response?.scope_type === 'channel' ? response?.scope_id || commonItemValue(items, 'channel_id') : '',
      userId: response?.scope_type === 'profile' ? response?.scope_id || commonItemValue(items, 'user_id') || commonItemValue(items, 'author_id') : '',
      channelLabel: response?.scope_type === 'channel' ? scopeLabel || commonItemValue(items, 'channel_title') : '',
      userLabel: response?.scope_type === 'profile' ? scopeLabel || commonItemValue(items, 'author_display_name') || commonItemValue(items, 'author_username') : '',
      telegramUsername: response?.scope_type === 'channel' ? commonItemValue(items, 'channel_username') : commonItemValue(items, 'author_username'),
    };
  }
  return {
    handle: authorScreenName,
    authorId: response?.user_id || commonItemValue(items, 'author_id'),
    displayName: response?.display_name || commonItemValue(items, 'author_display_name'),
    channelId: '',
    userId: '',
    channelLabel: '',
    userLabel: '',
    telegramUsername: '',
  };
}

function ContentScopeSummaryPanel({
  summary,
  platform,
  profileSource,
  scopeSource,
}: {
  summary: ContentScopeSummary;
  platform: string;
  profileSource?: SearchSource | null;
  scopeSource?: SearchSource | null;
}) {
  const profileUrl = profileSource ? detailUrl(profileSource, '') : '';
  const scopeUrl = scopeSource?.detail_enabled ? detailUrl(scopeSource, '') : '';
  const linkedProfileValue = (value?: string) => linkedMetaValue(value, profileUrl);
  const linkedScopeValue = (value?: string) => linkedMetaValue(value, scopeUrl);
  const telegramId = summary.channelId || summary.userId;
  const telegramIsChannel = Boolean(summary.channelId);
  const telegramHandle = summary.telegramUsername ? `@${summary.telegramUsername}` : '';
  const telegramName = telegramIsChannel ? summary.channelLabel : summary.userLabel;
  const rows: Array<[string, React.ReactNode]> = platform === 'telegram'
    ? [
      ['平台', platformLabel(platform)],
      [telegramIsChannel ? '频道 ID' : '用户 ID', linkedScopeValue(telegramId)],
      [telegramIsChannel ? '频道句柄' : '用户句柄', linkedScopeValue(telegramHandle)],
      [telegramIsChannel ? '频道名' : '用户名', linkedScopeValue(telegramName)],
    ].filter(([, value]) => value)
    : [
      ['平台', platformLabel(platform)],
      ['ID', linkedProfileValue(summary.authorId || summary.userId)],
      ['句柄', linkedProfileValue(summary.handle ? `@${summary.handle}` : '')],
      ['用户名', linkedProfileValue(summary.displayName || summary.userLabel)],
    ].filter(([, value]) => value);

  return (
    <div className="zhibi-content-scope-panel">
      {rows.map(([label, value]) => (
        <span className="zhibi-content-scope-field" key={label}>
          <Text type="secondary">{label}</Text>
          <Text strong>{value}</Text>
        </span>
      ))}
    </div>
  );
}

function compactContentMetaRows(
  rows: Array<[string, React.ReactNode | undefined | null | false]>,
): ContentMetaRow[] {
  return rows.filter(([, value]) => value !== undefined && value !== null && value !== false && value !== '') as ContentMetaRow[];
}

function TweetContentListItem({
  item,
  scopeSummary,
  authorProfileSource,
}: {
  item: TweetContentItem;
  scopeSummary?: ContentScopeSummary;
  authorProfileSource?: SearchSource | null;
}) {
  const isTelegram = item.platform === 'telegram';
  const telegramChannelLabel = isTelegram ? formatTelegramChannelLabel(item) : '';
  const telegramAuthorLabel = isTelegram ? formatTelegramAuthorLabel(item) : '';
  const authorProfileUrl = authorProfileSource ? detailUrl(authorProfileSource, '') : '';
  const telegramChannelUrl = item.channel_source?.detail_enabled ? detailUrl(item.channel_source, '') : '';
  const telegramUserUrl = item.user_source?.detail_enabled ? detailUrl(item.user_source, '') : '';
  const metrics = [
    ['点赞', item.favorite_count],
    ['转发', item.retweet_count],
    ['回复', item.reply_count],
    ['引用', item.quote_count],
    ['浏览', item.view_count],
  ].filter(([, value]) => value);
  const showAuthor = !isTelegram && item.author_screen_name && (
    !scopeSummary?.handle || item.author_screen_name.toLowerCase() !== scopeSummary.handle.toLowerCase()
  );
  const showAuthorId = item.author_id && item.author_id !== scopeSummary?.authorId;
  const showChannelLabel = isTelegram && telegramChannelLabel;
  const showAuthorLabel = isTelegram && telegramAuthorLabel;
  const isScopedTelegramChannel = isTelegram && item.channel_id && item.channel_id === scopeSummary?.channelId;
  const itemUserId = item.user_id || item.author_id;
  const isScopedTelegramUser = isTelegram && itemUserId && itemUserId === scopeSummary?.userId;
  const telegramChannelRows = compactContentMetaRows([
    ['ID', isScopedTelegramChannel ? '' : linkedMetaValue(item.channel_id, telegramChannelUrl)],
    ['句柄', linkedMetaValue(item.channel_username ? `@${item.channel_username}` : '', telegramChannelUrl)],
    ['频道名', linkedMetaValue(item.channel_title, telegramChannelUrl)],
  ]);
  const telegramAuthorRows = compactContentMetaRows([
    ['ID', isScopedTelegramUser ? '' : linkedMetaValue(itemUserId, telegramUserUrl)],
    ['句柄', linkedMetaValue(item.author_username ? `@${item.author_username}` : '', telegramUserUrl)],
    ['用户名', linkedMetaValue(item.author_display_name, telegramUserUrl)],
  ]);
  const telegramTechnicalRows = compactContentMetaRows([
    ['消息 ID', item.msg_id || item.content_id],
    ['回复消息 ID', item.in_reply_to_screen_name],
    ['库内更新时间', item.database_updated_at],
  ]);
  const detailRows = compactContentMetaRows(!isTelegram ? [
    ['推文 ID', item.tweet_id],
    ['作者 ID', showAuthorId ? linkedMetaValue(item.author_id, authorProfileUrl) : ''],
    ['句柄', showAuthor ? linkedMetaValue(item.author_screen_name ? `@${item.author_screen_name}` : '', authorProfileUrl) : ''],
    ['用户名', linkedMetaValue(item.author_display_name, authorProfileUrl)],
    ['会话 ID', item.conversation_id],
    ['回复对象', item.in_reply_to_screen_name ? `@${item.in_reply_to_screen_name}` : ''],
    ['客户端', item.source],
    ['库内更新时间', item.database_updated_at],
  ] : []);
  const hasTelegramDetails = telegramChannelRows.length > 0 || telegramAuthorRows.length > 0 || telegramTechnicalRows.length > 0;

  return (
    <List.Item className="zhibi-tweet-item">
      <Space direction="vertical" size={8} className="zhibi-full-width">
        <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
          <Space className="zhibi-content-item-header" wrap size={[6, 4]}>
            {showAuthor && (
              <Tag color="blue">
                {authorProfileUrl
                  ? <Link href={authorProfileUrl} className="zhibi-tag-link">@{item.author_screen_name}</Link>
                  : `@${item.author_screen_name}`}
              </Tag>
            )}
            {showChannelLabel && <Tag color="cyan">{linkedMetaValue(telegramChannelLabel, telegramChannelUrl)}</Tag>}
            {showAuthorLabel && <Tag color="blue">{linkedMetaValue(telegramAuthorLabel, telegramUserUrl)}</Tag>}
            {item.created_at && <Text type="secondary">{item.created_at}</Text>}
            {item.lang && <Tag>{item.lang}</Tag>}
          </Space>
          {item.url && <Link href={item.url} target="_blank" rel="noreferrer">在 X 查看</Link>}
        </Flex>
        <Paragraph className="zhibi-tweet-text">{item.text || '暂无正文'}</Paragraph>
        {metrics.length > 0 && (
          <Space className="zhibi-content-metrics" wrap size={[6, 4]}>
            {metrics.map(([label, value]) => <Tag key={label}>{label} {value}</Tag>)}
          </Space>
        )}
        {isTelegram && hasTelegramDetails && (
          <details className="zhibi-content-item-details">
            <summary>Telegram 字段</summary>
            <div className="zhibi-content-meta-groups">
              {telegramChannelRows.length > 0 && (
                <ContentMetaGroup title="频道" rows={telegramChannelRows} />
              )}
              {telegramAuthorRows.length > 0 && (
                <ContentMetaGroup title="用户" rows={telegramAuthorRows} />
              )}
              {telegramTechnicalRows.length > 0 && (
                <ContentMetaGroup title="消息" rows={telegramTechnicalRows} />
              )}
            </div>
          </details>
        )}
        {!isTelegram && detailRows.length > 0 && (
          <details className="zhibi-content-item-details">
            <summary>ID / 技术字段</summary>
            <div className="zhibi-content-item-meta">
              {detailRows.map(([label, value]) => (
                <span className="zhibi-content-meta-field" key={label}>
                  <Text type="secondary">{label}</Text>
                  <Text>{value}</Text>
                </span>
              ))}
            </div>
          </details>
        )}
      </Space>
    </List.Item>
  );
}

function ContentMetaGroup({ title, rows }: { title: string; rows: ContentMetaRow[] }) {
  return (
    <div className="zhibi-content-meta-group">
      <Text className="zhibi-content-meta-group-title" type="secondary">{title}</Text>
      <div className="zhibi-content-item-meta">
        {rows.map(([label, value]) => (
          <span className="zhibi-content-meta-field" key={`${title}-${label}`}>
            <Text type="secondary">{label}</Text>
            <Text>{value}</Text>
          </span>
        ))}
      </div>
    </div>
  );
}

function linkedMetaValue(value?: string, url?: string): React.ReactNode {
  if (!value) return '';
  return url ? <Link href={url}>{value}</Link> : value;
}

function formatTelegramChannelLabel(item: TweetContentItem): string {
  if (item.channel_title) return item.channel_title;
  if (item.channel_username) return `@${item.channel_username}`;
  return item.channel_id ? `频道 ${item.channel_id}` : '';
}

function formatTelegramAuthorLabel(item: TweetContentItem): string {
  if (item.author_display_name) return item.author_display_name;
  if (item.author_username) return `@${item.author_username}`;
  return item.user_id ? `用户 ${item.user_id}` : '';
}

function TweetDetailApp({ mongoId }: { mongoId: string }) {
  const [detail, setDetail] = useState<TweetDetailResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const returnUrl = readReturnUrl();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`/data/api/content/twitter/tweet/${encodeURIComponent(mongoId)}/`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then((payload: TweetDetailResponse) => {
            throw new Error(payload.message || `HTTP ${res.status}`);
          });
        }
        return res.json() as Promise<TweetDetailResponse>;
      })
      .then((payload) => setDetail(payload))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message || '内容详情加载失败。');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [mongoId]);

  const item = detail?.item;
  const profileUrl = detail?.profile_source ? detailUrl(detail.profile_source, '') : '';

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Flex className="zhibi-page-nav" justify="space-between" align="center" gap={12} wrap="wrap">
            <Breadcrumb
              items={[
                { title: <a href="/">首页</a> },
                { title: '数据中心' },
                { title: <a href={returnUrl}>跨平台检索</a> },
                { title: '内容详情' },
              ]}
            />
            <Button href={returnUrl} icon={<ArrowLeftOutlined />}>返回检索结果</Button>
          </Flex>
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Space wrap>
                <Tag color="blue">Twitter</Tag>
                <Tag>具体内容</Tag>
              </Space>
              <Title level={2}>推文详情</Title>
              {item?.author_screen_name && (
                <Paragraph type="secondary">
                  作者：{profileUrl ? <Link href={profileUrl}>@{item.author_screen_name}</Link> : `@${item.author_screen_name}`}
                </Paragraph>
              )}
            </div>
          </Flex>
        </Card>

        {loading && <Spin tip="正在加载内容详情..." />}
        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}
        {!loading && !error && !item && <Empty description="未找到对应内容。" />}
        {!loading && item && (
          <Card className="zhibi-section-card" title="内容详情">
            <TweetContentListItem item={item} authorProfileSource={detail?.profile_source} />
          </Card>
        )}
      </div>
    </ConfigProvider>
  );
}

const searchRoot = document.getElementById('data-center-root');
if (searchRoot) {
  createRoot(searchRoot).render(<SearchApp />);
}

const detailRoot = document.getElementById('entity-detail-root');
if (detailRoot) {
  createRoot(detailRoot).render(
    <EntityDetailApp
      alias={detailRoot.dataset.alias ?? ''}
      collection={detailRoot.dataset.collection ?? ''}
      mongoId={detailRoot.dataset.mongoId ?? ''}
    />,
  );
}

const contentRoot = document.getElementById('entity-content-root');
if (contentRoot) {
  createRoot(contentRoot).render(
    <EntityContentApp
      platform={contentRoot.dataset.platform || 'twitter'}
      alias={contentRoot.dataset.alias}
      collection={contentRoot.dataset.collection}
      mongoId={contentRoot.dataset.mongoId}
      screenName={contentRoot.dataset.screenName}
    />,
  );
}

const tweetDetailRoot = document.getElementById('tweet-detail-root');
if (tweetDetailRoot) {
  createRoot(tweetDetailRoot).render(
    <TweetDetailApp mongoId={tweetDetailRoot.dataset.mongoId ?? ''} />,
  );
}
